import { Agent } from "./AgentsLeaderboard";
import CediSign from "../../components/elements/CediSign";
import { AGENT_ACHIEVEMENT_STATUS } from "../../resources/enums";
import Avatar from "../../components/elements/Avatar";
import { getNameInitials, numberWithCommas } from "../../utilities/miscFunctions";

interface Props {
    agents: Agent[];
}

export default function LeaderboardTable({ agents }: Props) {

    return (
        <div className="overflow-auto max-h-80">
            <table className="min-w-full">

                <thead className="border-y bg-gray-50 dark:bg-primary-dark-secondary dark:border-primary-dark-border_active">

                    <tr className="text-xs uppercase tracking-wider text-gray-500 dark:text-text-dark-secondary">

                        <th className="px-4 py-4 text-left">Rank</th>

                        <th className="px-4 py-4 text-left min-w-[6rem]">Agent</th>

                        <th className="px-4 py-4 text-right">Target</th>

                        <th className="px-4 py-4 text-right">Actual</th>

                        <th className="px-4 py-4 text-right">Variance</th>

                        <th className="px-4 py-4 text-center w-28">
                            Achievement
                        </th>

                    </tr>

                </thead>

                <tbody>
                    {agents.map((agent) => {
                        const status = getStatus(agent.status);

                        return (
                            <tr
                                key={agent.id}
                                className="border-b dark:border-b-primary-dark-border_active hover:bg-gray-50 hover:dark:bg-primary-dark-border_active transition"
                            >
                                {/* Rank */}
                                <td className="px-2 py-5 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        {agent.rank === 1
                                            ? "🥇"
                                            : agent.rank === 2
                                                ? "🥈"
                                                : agent.rank === 3
                                                    ? "🥉"
                                                    : `#${agent.rank}`}
                                    </div>
                                </td>

                                {/* Agent */}
                                <td className="px-6 py-5">
                                    <div className="flex items-center gap-4">
                                        <Avatar
                                            src={agent.avatar}
                                            nameInitials={getNameInitials(agent.name)}
                                            size="sm"
                                        />

                                        <div>
                                            <p className="font-semibold text-xs"> {agent.name}</p>
                                            <p className="text-[10px] text-gray-500 dark:text-text-dark-secondary">{agent.branch} Branch</p>
                                        </div>
                                    </div>
                                </td>

                                {/* Target */}
                                <td className="px-6 py-5 text-right font-medium text-xs whitespace-nowrap">
                                    <CediSign /> {numberWithCommas(agent.target)}
                                </td>

                                {/* Actual */}
                                <td className="px-6 py-5 text-right text-xs whitespace-nowrap">
                                    <div className="font-semibold">
                                        <CediSign /> {numberWithCommas(agent.actual)}
                                    </div>

                                </td>

                                {/* Variance */}
                                <td className="px-6 py-5 text-right text-sm">

                                    <span
                                        className={`font-semibold whitespace-nowrap ${agent.variance >= 0
                                            ? "text-green-600"
                                            : "text-red-600"
                                            }`}
                                    >
                                        {agent.variance >= 0 ? "+" : "-"}
                                        <CediSign /> {numberWithCommas(Math.abs(agent.variance))}

                                    </span>
                                </td>

                                {/* Progress */}
                                <td className="px-6 py-5">
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="font-semibold text-[10px]">
                                                {agent.achievement}%
                                            </span>

                                            <span
                                                className={`px-3 py-1 rounded-full text-[10px] font-semibold ${status.color}`}
                                            >
                                                {status.label}
                                            </span>
                                        </div>

                                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                                            <div
                                                className={`h-full ${status.progress}`}
                                                style={{
                                                    width: `${Math.min(
                                                        agent.achievement,
                                                        100
                                                    )}%`,
                                                }}
                                            />
                                        </div>

                                        <div className="text-[10px] text-gray-500 dark:text-text-dark-primary">

                                            <CediSign /> {numberWithCommas(agent.actual)}

                                            {" / "}

                                            <CediSign /> {numberWithCommas(agent.target)}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export const getStatus = (status: AGENT_ACHIEVEMENT_STATUS) => {
    if (status === AGENT_ACHIEVEMENT_STATUS.EXCEEDED)
        return {
            label: "Exceeded",
            color: "bg-green-100 text-green-700",
            progress: "bg-green-500",
        };
    if (status === AGENT_ACHIEVEMENT_STATUS.ACHIEVED)
        return {
            label: "Achieved",
            color: "bg-green-100 text-green-700",
            progress: "bg-green-500",
        };

    if (status === AGENT_ACHIEVEMENT_STATUS.ON_TRACK)
        return {
            label: "On Track",
            color: "bg-blue-100 text-blue-700",
            progress: "bg-blue-500",
        };

    if (status === AGENT_ACHIEVEMENT_STATUS.CATCHING_UP)
        return {
            label: "Catching Up",
            color: "bg-amber-100 text-amber-700",
            progress: "bg-amber-500",
        };

    return {
        label: "Behind",
        color: "bg-red-100 text-red-700",
        progress: "bg-red-500",
    };
};